import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Sparkles, GraduationCap, Bot, Brain, ShieldCheck, Clock, Check, ArrowRight,
  Loader2, Lock, FileText, Mail, Award, Timer,
} from "lucide-react";

function pad(n) { return String(n).padStart(2, "0"); }

function Countdown({ until, compact = false, className = "", labelSuffix = "until paid pricing kicks in", color = "emerald" }) {
  const target = (() => {
    try { return new Date(until + "T23:59:59").getTime(); }
    catch { return 0; }
  })();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hrs  = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  if (diff <= 0) return null;

  if (compact) {
    return (
      <div data-testid="countdown-compact" className={`flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 ${className}`}>
        <Timer size={14} className="text-amber-700 shrink-0" strokeWidth={3}/>
        <div className="text-xs text-amber-900">
          <span className="font-black">{days}d {pad(hrs)}h {pad(mins)}m {pad(secs)}s</span>
          <span className="font-bold opacity-80"> {labelSuffix}</span>
        </div>
      </div>
    );
  }

  const accent = color === "amber"
    ? { text: "text-amber-700", border: "border-amber-100" }
    : { text: "text-emerald-700", border: "border-emerald-100" };

  const Cell = ({ label, value }) => (
    <div className={`bg-white rounded-xl px-3 py-2 text-center min-w-[3.5rem] border ${accent.border}`}>
      <div className={`text-2xl font-black leading-none tabular-nums ${accent.text}`}>{value}</div>
      <div className="text-[9px] uppercase font-bold tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );

  return (
    <div data-testid="countdown" className={`flex items-center gap-2 ${className}`}>
      <Cell label="Days"  value={days}/>
      <Cell label="Hours" value={pad(hrs)}/>
      <Cell label="Mins"  value={pad(mins)}/>
      <Cell label="Secs"  value={pad(secs)}/>
    </div>
  );
}

export default function ProfessionalDevelopment() {
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ teacher_name: "", email: "", school: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/pd/info").then(r => setInfo(r.data)).catch(() => {});
  }, []);

  const enrollFree = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pd/enroll", { ...form, origin_url: window.location.origin });
      setDone(data);
      toast.success("You're in! Check your email for the course link.");
      setTimeout(() => nav(`/pd/course?token=${data.access_token}`), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enrollment failed");
    } finally { setBusy(false); }
  };

  const buyNow = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pd/checkout", { ...form, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const isFree = !!info?.is_free_now;
  const isSummer = !!info?.is_summer_special;
  const submit = isFree ? enrollFree : buyNow;

  const summerEndLabel = (() => {
    if (!info?.summer_until) return "September 30, 2026";
    try {
      const d = new Date(info.summer_until + "T23:59:59");
      return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    } catch { return info.summer_until; }
  })();

  return (
    <div data-testid="pd-page" className="relative overflow-hidden">
      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-16">
        <div className="blob -top-32 -left-32 w-96 h-96 bg-violet-200" />
        <div className="blob top-20 -right-24 w-[28rem] h-[28rem] bg-amber-200" />

        <div className="relative grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 animate-pop">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-100 border border-violet-200 mb-6">
              <Sparkles size={14} className="text-violet-700" strokeWidth={3}/>
              <span className="text-xs font-black uppercase tracking-[0.2em] text-violet-800">Professional Development for Educators</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-slate-900 leading-[1.05]">
              AI Foundation for Teachers
              <span className="block mt-2 bg-gradient-to-r from-violet-600 to-amber-500 bg-clip-text text-transparent">
                Do not get left behind.
              </span>
            </h1>
            <p className="mt-7 text-lg lg:text-xl text-slate-600 max-w-xl leading-relaxed">
              A self-paced, classroom-ready PD course that gets you fluent in AI tools, ethics,
              and the practical ways AI helps you teach and free up hours of prep time. Made for K–12 educators
              by The Haddis Academy.
            </p>

            {info && (
              <div className="mt-8 inline-flex flex-col gap-3 px-5 py-4 rounded-2xl bg-amber-50 border border-amber-200 max-w-md">
                {isFree ? (
                  <>
                    <div className="flex items-center gap-3">
                      <Award className="text-emerald-600 shrink-0" strokeWidth={2.5}/>
                      <div className="text-left">
                        <div className="font-black text-emerald-900 text-base">FREE until May 15, 2026</div>
                        <div className="text-xs text-emerald-800">Then ${info.price_usd} one-time · lifetime access</div>
                      </div>
                    </div>
                    <Countdown until={info.free_until} />
                  </>
                ) : isSummer ? (
                  <>
                    <div className="flex items-center gap-3">
                      <Sparkles className="text-amber-600 shrink-0" strokeWidth={2.5}/>
                      <div className="text-left">
                        <div className="font-black text-amber-900 text-base">🌞 End-of-Summer Special · ${info.price_usd?.toFixed?.(2) ?? info.price_usd}</div>
                        <div className="text-xs text-amber-800">
                          <span className="line-through opacity-70">${info.original_price_usd?.toFixed?.(2) ?? "69.99"}</span>{" "}
                          · Lifetime access · Ends {summerEndLabel}
                        </div>
                      </div>
                    </div>
                    <Countdown until={info.summer_until} color="amber" />
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <Lock className="text-emerald-600 shrink-0" strokeWidth={2.5}/>
                    <div className="text-left">
                      <div className="font-black text-emerald-900 text-sm">${info.price_usd} one-time</div>
                      <div className="text-xs text-emerald-800">Lifetime access · learn at your pace</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {[
                { icon: Brain, title: "Foundations of AI", body: "Plain-English mental models. No PhD required." },
                { icon: Bot, title: "Practical classroom use", body: "Lesson plans, IEP drafting, parent emails, rubrics." },
                { icon: ShieldCheck, title: "Ethics & safety", body: "What to share, what not to. Student privacy first." },
              ].map((f, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                  <f.icon className="text-violet-600 mb-3" size={22} strokeWidth={2.5}/>
                  <div className="font-black text-slate-900 text-sm">{f.title}</div>
                  <div className="text-xs text-slate-600 mt-1">{f.body}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Enrollment card */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-[0_20px_60px_rgba(124,58,237,0.18)] border border-slate-100 sticky top-28">
              {done ? (
                <div data-testid="pd-success" className="text-center py-6">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
                    <Check className="text-emerald-600" strokeWidth={3}/>
                  </div>
                  <h3 className="text-2xl font-black text-slate-900">You're enrolled!</h3>
                  <p className="text-slate-600 mt-2">Sending you to the course now…</p>
                  <Loader2 className="w-5 h-5 mx-auto mt-3 animate-spin text-slate-400"/>
                </div>
              ) : (
                <>
                  <div className="text-xs uppercase tracking-widest font-black text-violet-700 mb-2">
                    {isFree ? "Free enrollment" : isSummer ? "🌞 Summer Special" : "Buy course"}
                  </div>
                  <h3 className="text-3xl font-black text-slate-900 mb-1">
                    {isFree ? "Get your FREE seat" : (
                      <>
                        Enroll for ${info?.price_usd?.toFixed?.(2) ?? info?.price_usd ?? "19.99"}
                        {isSummer && (
                          <span className="ml-2 text-lg font-bold text-slate-400 line-through">${info?.original_price_usd?.toFixed?.(2) ?? "69.99"}</span>
                        )}
                      </>
                    )}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    {isFree
                      ? <>Limited window: <b>FREE until May 15, 2026</b>, then <b>${info?.price_usd ?? "69.99"} one-time</b>.</>
                      : isSummer
                        ? <>End-of-summer special: <b>${info?.price_usd?.toFixed?.(2) ?? "19.99"} one-time</b> until <b>{summerEndLabel}</b>, then <b>${info?.original_price_usd?.toFixed?.(2) ?? "69.99"}</b>.</>
                        : "One-time payment · lifetime access."}
                  </p>
                  {isFree && info?.free_until && <Countdown until={info.free_until} compact className="mb-5"/>}
                  {isSummer && info?.summer_until && <Countdown until={info.summer_until} compact className="mb-5" labelSuffix="left at this price"/>}
                  <form onSubmit={submit} className="space-y-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Your name</Label>
                      <Input data-testid="pd-name" required value={form.teacher_name}
                        onChange={(e) => setForm({ ...form, teacher_name: e.target.value })}
                        className="mt-2 rounded-xl h-12 border-slate-200" placeholder="Ms. Jane Doe" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</Label>
                      <Input data-testid="pd-email" type="email" required value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="mt-2 rounded-xl h-12 border-slate-200" placeholder="you@school.edu" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">School</Label>
                        <Input data-testid="pd-school" value={form.school}
                          onChange={(e) => setForm({ ...form, school: e.target.value })}
                          className="mt-2 rounded-xl h-12 border-slate-200" placeholder="(optional)" />
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Role</Label>
                        <Input data-testid="pd-role" value={form.role}
                          onChange={(e) => setForm({ ...form, role: e.target.value })}
                          className="mt-2 rounded-xl h-12 border-slate-200" placeholder="(optional)" />
                      </div>
                    </div>
                    <Button type="submit" disabled={busy} data-testid="pd-submit"
                      className={`w-full rounded-full h-12 font-black text-base shadow-lg ${isFree ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-200" : "bg-violet-600 hover:bg-violet-700 text-white shadow-violet-200"}`}>
                      {busy ? <Loader2 className="w-5 h-5 animate-spin"/> : (
                        <>
                          {isFree ? "Get free access" : `Pay $${info?.price_usd?.toFixed?.(2) ?? info?.price_usd ?? "19.99"}`}
                          <ArrowRight className="ml-2" size={16} strokeWidth={3}/>
                        </>
                      )}
                    </Button>
                    <div className="text-center text-xs text-slate-500 mt-2">
                      <Mail size={11} className="inline -mt-0.5 mr-1" strokeWidth={3}/>
                      You'll get a course link at the email above.
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What's inside */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-slate-900 text-white rounded-3xl p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-violet-500/30 rounded-full blur-3xl"/>
          <div className="relative">
            <div className="text-xs font-black uppercase tracking-[0.22em] text-amber-300 mb-3">What's inside the course</div>
            <h2 className="text-4xl lg:text-5xl font-black tracking-tight mb-10">A practical PD that respects your time.</h2>
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-5">
              {[
                "What AI actually is (and isn't) — without the hype",
                "Hands-on tour of ChatGPT, Gemini, Claude — when to pick which",
                "Lesson planning prompts you can copy & paste tomorrow",
                "Drafting IEPs, 504 plans, and parent communication 10x faster",
                "Building rubrics, exit tickets, and differentiated worksheets",
                "Detecting AI in student work — without becoming the AI police",
                "Student-safe AI activities by grade band (K–2, 3–5, 6–8, 9–12)",
                "Privacy, FERPA, COPPA — what to share, what to keep off",
                "Building YOUR personal teaching assistant in 15 minutes",
                "Certificate of completion you can show your district",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="mt-0.5 w-6 h-6 rounded-full bg-amber-400/20 text-amber-300 flex items-center justify-center shrink-0">
                    <Check size={12} strokeWidth={3}/>
                  </div>
                  <div className="text-slate-100">{item}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-10 text-center text-sm text-slate-500">
        <div className="font-bold text-slate-700 mb-1">The Haddis Academy LLC</div>
        <div>newagelearning@thehaddisacademy.com · (571) 466-7348</div>
      </footer>
    </div>
  );
}
